import { z } from 'zod';
import { TFunction } from 'i18next';

import { LocationAddressFilterType } from '@/entities/location';

/** Optional numeric field: NaN (empty input read with `valueAsNumber`), an empty string and null (empty value from the API) all mean "not filled in". */
const optionalNumeric = z.preprocess(
  (value) => (value == null || value === '' || (typeof value === 'number' && Number.isNaN(value)) ? undefined : value),
  z.union([z.number(), z.string()]).optional()
);

export const createConsignmentSchema = (t: TFunction) =>
  z.object({
    // Consignment Identification
    // locationGroup: z.string().optional(), // Static field from siteId
    // createdBy: z.string().optional(), // Static field from consignment object
    // trackingCode: z.string().optional(), // Static field from consignment object
    ownersReference: z.string().optional(),

    // Consignment Control
    priority: z.enum(['normal', 'high', 'urgent']).default('normal'),
    remarks: z.string().optional(),
    cmrLinks: z.string().max(255).optional().nullable(),
    transportUnitQuantity: optionalNumeric,

    // Point of Contact
    senderOrganization: z.string().min(1, t('validity.valueMissing.defaultMessage')),
    senderContact: z.string().min(1, t('validity.valueMissing.defaultMessage')),
    receiverOrganization: z.string().min(1, t('validity.valueMissing.defaultMessage')),
    receiverContact: z.string().min(1, t('validity.valueMissing.defaultMessage')),
    clearingAgent: z.string().optional(),
    clearingAgentContact: z.string().optional(),
    onBehalfOf: z.string().optional(),

    // Starting Point
    startingPointType: z.enum(
      [
        LocationAddressFilterType.CUSTOMER_ADDRESS,
        LocationAddressFilterType.ARRIVAL_PORT_TRANSSHIPMENT,
        LocationAddressFilterType.ARRIVAL_DELIVERY_OR_PICKUP_AT_SITE
      ],
      {
        errorMap: () => ({ message: t('validity.valueMissing.defaultMessage') })
      }
    ),
    arrivingTo: z.string().min(1, t('validity.valueMissing.defaultMessage')), // Location ID from location addresses API
    expectedArrivalDate: z.date().or(z.string()).optional(),
    documentNo: z.string().optional(),
    contactInfo: z.string().optional(),
    instructions: z.string().optional(),

    // Service Identification
    serviceType: z.enum(
      [
        LocationAddressFilterType.CUSTOMER_ADDRESS,
        LocationAddressFilterType.WAREHOUSE_PICKUP,
        LocationAddressFilterType.STORAGE
      ],
      {
        errorMap: () => ({ message: t('validity.valueMissing.defaultMessage') })
      }
    ),
    serviceDestination: z.string().min(1, t('validity.valueMissing.defaultMessage')),
    durationInDays: optionalNumeric,
    serviceInstructions: z.string().optional(),

    // Planned Movement
    onSiteDate: z.date().or(z.string()).optional(),
    plannedMode: z.enum(['none', 'air', 'road', 'sea', 'rail']).default('none'),
    missionTransporterNumber: z.string().optional()
  });

export type ConsignmentFormValues = z.infer<ReturnType<typeof createConsignmentSchema>>;
